"use client";
import React from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  Avatar,
  Stack,
  Divider,
  Chip,
  useMediaQuery,
  useTheme,
  Button,
} from "@mui/material";
import { motion } from "framer-motion";
import {
  EmojiEvents,
  People,
  Assignment,
  Gavel,
  Business,
  Verified,
  Speed,
  Security,
  TrendingUp,
  Headset,
  School,
  ThumbUp,
} from "@mui/icons-material";
import { useLanguage } from "../Component/LanguageProvider";



export const AboutUsPage = () => {
  const { currentLang, isInitialized } = useLanguage();
  const isRTL = currentLang === "ar";
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 15,
      },
    },
  };

  // Translations
  const translations = {
    en: {
      heroTitle: "About Us",
      heroSubtitle: "RUKN ALMUROOJ Documents Clearing Services",
      heroDesc: "Your trusted partner for administrative and legal document clearing services in Dubai and across the UAE.",
      whoWeAre: "Who We Are",
      whoWeAreTitle: "We Provide Professional",
      whoWeAreTitleLine2: "Documents Clearing Services",
      whoWeAreDesc1: "RUKN ALMUROOJ Documents Clearing Services is a licensed company providing integrated solutions for government and judicial administrative transactions with accuracy and professionalism.",
      whoWeAreDesc2: "We are committed to providing distinguished services according to the highest quality and transparency standards, without practicing law or judicial representation.",
      ourMission: "Our Mission",
      ourVision: "Our Vision",
      missionDesc: "To provide exceptional administrative and document clearing services that exceed client expectations, ensuring accuracy, efficiency, and complete satisfaction.",
      visionDesc: "To become the leading provider of document clearing services in the UAE, recognized for our professionalism, reliability, and commitment to excellence.",
      whyChooseUs: "Why Choose Us",
      whyChooseUsDesc: "What makes us different from others",
      features: [
        // { icon: Verified, title: "Licensed & Certified", desc: "Officially licensed by Creative City – Fujairah" },
        { icon: Speed, title: "Fast & Efficient", desc: "Quick turnaround time for all services" },
        { icon: Security, title: "100% Confidential", desc: "Your documents and data are fully protected" },
        { icon: TrendingUp, title: "Experienced Team", desc: "Led by Hatem Elsegaei with years of expertise" },
        { icon: Headset, title: "24/7 Support", desc: "Always here to assist you with your needs" },
        { icon: School, title: "Professional Staff", desc: "Trained professionals handling your documents" },
      ],
      ourValues: "Our Values",
      values: [
        { icon: ThumbUp, title: "Integrity", desc: "We act with honesty and transparency" },
        { icon: Security, title: "Excellence", desc: "Striving for the highest quality standards" },
        { icon: People, title: "Client Focus", desc: "Your satisfaction is our priority" },
        { icon: EmojiEvents, title: "Innovation", desc: "Embracing modern solutions for better service" },
      ],
      statsTitle: "Our Achievements",
      stats: [
        { value: "15+", label: "Years of Experience" },
        { value: "500+", label: "Happy Clients" },
        { value: "5,000+", label: "Documents Processed" },
        { value: "100%", label: "Client Satisfaction" },
      ],
      leadershipTitle: "Leadership",
      leadershipDesc: "Meet our experienced team leader",
      leaderName: "Hatem Elsegaei",
      leaderTitle: "Head of Legal Department",
      leaderDesc: "With over 15 years of experience in legal and administrative services, Hatem leads our team with dedication and expertise, ensuring the highest quality of service delivery.",
      ctaTitle: "Ready to work with us?",
      ctaDesc: "Let us help you with your document clearing needs",
      ctaButton: "Contact Us Today",
      footerNote: "Note: We do not provide legal advice or legal drafting. Any legal documents are printed based on texts provided by the client.",
    },
    ar: {
      heroTitle: "من نحن",
      heroSubtitle: "ركن المروج لخدمات تصفية المعاملات",
      heroDesc: "شريكك الموثوق لخدمات تصفية المعاملات الإدارية والقانونية في دبي والإمارات.",
      whoWeAre: "من نحن",
      whoWeAreTitle: "نقدم خدمات احترافية",
      whoWeAreTitleLine2: "لتصفية المعاملات",
      whoWeAreDesc1: "ركن المروج لخدمات تصفية المعاملات هي شركة مرخصة تقدم حلولاً متكاملة للمعاملات الحكومية والقضائية الإدارية بدقة واحترافية.",
      whoWeAreDesc2: "نلتزم بتقديم خدمات متميزة وفق أعلى معايير الجودة والشفافية، دون ممارسة المحاماة أو التمثيل القضائي.",
      ourMission: "رسالتنا",
      ourVision: "رؤيتنا",
      missionDesc: "تقديم خدمات إدارية وتوثيق استثنائية تتجاوز توقعات العملاء، مع ضمان الدقة والكفاءة والرضا التام.",
      visionDesc: "أن نصبح المزود الرائد لخدمات تصفية المعاملات في الإمارات، معترفاً بنا لاحترافيتنا وموثوقيتنا والتزامنا بالتميز.",
      whyChooseUs: "لماذا تختارنا",
      whyChooseUsDesc: "ما الذي يميزنا عن الآخرين",
      features: [
        { icon: Verified, title: "مرخص ومعتمد", desc: "مرخص رسمياً من مدينة كريتيف – الفجيرة" },
        { icon: Speed, title: "سريع وفعال", desc: "وقت استجابة سريع لجميع الخدمات" },
        { icon: Security, title: "سري بنسبة 100%", desc: "مستنداتك وبياناتك محمية بالكامل" },
        { icon: TrendingUp, title: "فريق ذو خبرة", desc: "بقيادة حاتم الصغيري مع سنوات من الخبرة" },
        { icon: Headset, title: "دعم على مدار الساعة", desc: "دائماً هنا لمساعدتك في احتياجاتك" },
        { icon: School, title: "موظفون محترفون", desc: "محترفون مدربون يتعاملون مع مستنداتك" },
      ],
      ourValues: "قيمنا",
      values: [
        { icon: ThumbUp, title: "النزاهة", desc: "نتصرف بأمانة وشفافية" },
        { icon: Security, title: "التميز", desc: "نسعى لأعلى معايير الجودة" },
        { icon: People, title: "التركيز على العميل", desc: "رضاك هو أولويتنا" },
        { icon: EmojiEvents, title: "الابتكار", desc: "نتبنى الحلول الحديثة لخدمة أفضل" },
      ],
      statsTitle: "إنجازاتنا",
      stats: [
        { value: "١٥+", label: "سنوات خبرة" },
        { value: "٥٠٠+", label: "عميل سعيد" },
        { value: "٥,٠٠٠+", label: "مستند تمت معالجته" },
        { value: "١٠٠%", label: "رضا العملاء" },
      ],
      leadershipTitle: "القيادة",
      leadershipDesc: "تعرف على قائد فريقنا ذو الخبرة",
      leaderName: "حاتم الصغيري",
      leaderTitle: "رئيس القسم القانوني",
      leaderDesc: "مع أكثر من 15 عاماً من الخبرة في الخدمات القانونية والإدارية، يقود حاتم فريقنا بتفان وخبرة، لضمان أعلى جودة في تقديم الخدمات.",
      ctaTitle: "هل أنت مستعد للعمل معنا؟",
      ctaDesc: "دعنا نساعدك في احتياجات تصفية المستندات الخاصة بك",
      ctaButton: "اتصل بنا اليوم",
      footerNote: "ملاحظة: لا نقدم استشارات قانونية ولا صياغة قانونية، وأي مستندات قانونية يتم طباعتها بناءً على النصوص التي يقدمها العميل.",
    },
  };

  const t = translations[currentLang];

  const getCardWidth = () => {
    if (isMobile) return "100%";
    if (isTablet) return "calc(50% - 16px)";
    return "calc(33.33% - 24px)";
  };

  const getValueCardWidth = () => {
    if (isMobile) return "100%";
    if (isTablet) return "calc(50% - 16px)";
    return "calc(25% - 24px)";
  };

  if (!isInitialized) {
    return null;
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #f5f7fa 0%, #e9eef5 100%)",
        pt: { xs: 10, md: 12 },
        pb: { xs: 6, md: 8 },
      }}
    >
      <Container maxWidth="xl">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
        >
          {/* Hero Section */}
          <motion.div variants={itemVariants}>
            <Box sx={{ textAlign: "center", mb: 6 }}>
              <Typography
                variant="h2"
                sx={{
                  fontFamily: "Cairo, Roboto",
                  fontWeight: "bold",
                  color: "#1a1a2e",
                  mb: 2,
                  fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" },
                }}
              >
                {t.heroTitle}
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  fontFamily: "Cairo, Roboto",
                  color: "#c9a03d",
                  fontWeight: 600,
                  mb: 2,
                }}
              >
                {t.heroSubtitle}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontFamily: "Cairo, Roboto",
                  color: "#666",
                  maxWidth: "800px",
                  mx: "auto",
                  lineHeight: 1.8,
                }}
              >
                {t.heroDesc}
              </Typography>
              <Divider
                sx={{
                  width: "80px",
                  margin: "0 auto",
                  height: "4px",
                  background: "linear-gradient(90deg, #c9a03d, #a07d2e)",
                  borderRadius: "2px",
                  mt: 3,
                }}
              />
            </Box>
          </motion.div>

          {/* Who We Are Section */}
          <Grid container spacing={6} alignItems="center" sx={{ mb: 10 }}>
            <Grid item xs={12} md={6}>
              <motion.div variants={itemVariants}>
                <Box
                  sx={{
                    background: "linear-gradient(145deg, #fff, #f8f6f0)",
                    borderRadius: "28px",
                    p: { xs: 3, md: 4 },
                    textAlign: "center",
                    boxShadow: "0 10px 30px rgba(0,0,0,0.05)",
                  }}
                >
                  <Gavel sx={{ fontSize: 60, color: "#c9a03d", mb: 2 }} />
                  <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                    {t.heroSubtitle}
                  </Typography>
                  <Typography sx={{ color: "#666" }}>
                    {t.heroDesc}
                  </Typography>
                </Box>
              </motion.div>
            </Grid>
            <Grid item xs={12} md={6}>
              <motion.div variants={itemVariants}>
                <Box
                  sx={{
                    display: "inline-flex",
                    bgcolor: "rgba(201,160,61,0.1)",
                    borderRadius: "50px",
                    px: 2,
                    py: 0.5,
                    mb: 2,
                  }}
                >
                  <Typography variant="body2" sx={{ color: "#c9a03d", fontWeight: 500 }}>
                    {t.whoWeAre}
                  </Typography>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { xs: "1.8rem", sm: "2.2rem", md: "2.5rem" },
                    fontWeight: "bold",
                    color: "#1a1a2e",
                    mb: 3,
                  }}
                >
                  {t.whoWeAreTitle}
                  <br />
                  {t.whoWeAreTitleLine2}
                </Typography>
                <Typography variant="body1" sx={{ color: "#666", lineHeight: 1.8, mb: 3 }}>
                  {t.whoWeAreDesc1}
                </Typography>
                <Typography variant="body1" sx={{ color: "#666", lineHeight: 1.8, mb: 4 }}>
                  {t.whoWeAreDesc2}
                </Typography>
                <Stack spacing={2}>
                  {/* <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Verified sx={{ color: "#c9a03d" }} />
                    <Typography sx={{ color: "#333" }}>Licensed by Creative City – Fujairah</Typography>
                  </Box> */}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <People sx={{ color: "#c9a03d" }} />
                    <Typography sx={{ color: "#333" }}>Expert team led by Hatem Elsegaei</Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Assignment sx={{ color: "#c9a03d" }} />
                    <Typography sx={{ color: "#333" }}>100% Administrative & Documentation Services</Typography>
                  </Box>
                </Stack>
              </motion.div>
            </Grid>
          </Grid>

          {/* ==================== Mission & Vision Section - كروت متساوية ==================== */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "stretch",
              flexWrap: "wrap",
              gap: { xs: 2, sm: 3, md: 4 },
              mb: 10,
            }}
          >
            {/* Mission Card */}
            <motion.div
              variants={itemVariants}
              style={{
                width: getCardWidth(),
                maxWidth: isMobile ? "100%" : "400px",
                minWidth: isMobile ? "auto" : "300px",
              }}
            >
              <Paper
                elevation={3}
                sx={{
                  p: { xs: 3, md: 4 },
                  borderRadius: "20px",
                  background: "white",
                  height: "100%",
                  minHeight: 280,
                  display: "flex",
                  flexDirection: "column",
                  textAlign: "center",
                  transition: "0.3s",
                  "&:hover": {
                    transform: "translateY(-5px)",
                    boxShadow: "0 15px 35px rgba(201,160,61,0.15)",
                  },
                }}
              >
                <Box
                  sx={{
                    width: 70,
                    height: 70,
                    borderRadius: "50%",
                    bgcolor: "rgba(201,160,61,0.1)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto 16px",
                  }}
                >
                  <TrendingUp sx={{ fontSize: 35, color: "#c9a03d" }} />
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: "bold",
                    color: "#1a1a2e",
                    mb: 2,
                    fontSize: "1.6rem",
                  }}
                >
                  {t.ourMission}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: "#666",
                    lineHeight: 1.8,
                    flex: 1,
                    wordWrap: "break-word",
                    whiteSpace: "normal",
                    overflowWrap: "break-word",
                  }}
                >
                  {t.missionDesc}
                </Typography>
              </Paper>
            </motion.div>

            {/* Vision Card */}
            <motion.div
              variants={itemVariants}
              style={{
                width: getCardWidth(),
                maxWidth: isMobile ? "100%" : "400px",
                minWidth: isMobile ? "auto" : "300px",
              }}
            >
              <Paper
                elevation={3}
                sx={{
                  p: { xs: 3, md: 4 },
                  borderRadius: "20px",
                  background: "white",
                  height: "100%",
                  minHeight: 280,
                  display: "flex",
                  flexDirection: "column",
                  textAlign: "center",
                  transition: "0.3s",
                  "&:hover": {
                    transform: "translateY(-5px)",
                    boxShadow: "0 15px 35px rgba(201,160,61,0.15)",
                  },
                }}
              >
                <Box
                  sx={{
                    width: 70,
                    height: 70,
                    borderRadius: "50%",
                    bgcolor: "rgba(201,160,61,0.1)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto 16px",
                  }}
                >
                  <EmojiEvents sx={{ fontSize: 35, color: "#c9a03d" }} />
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: "bold",
                    color: "#1a1a2e",
                    mb: 2,
                    fontSize: "1.6rem",
                  }}
                >
                  {t.ourVision}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: "#666",
                    lineHeight: 1.8,
                    flex: 1,
                    wordWrap: "break-word",
                    whiteSpace: "normal",
                    overflowWrap: "break-word",
                  }}
                >
                  {t.visionDesc}
                </Typography>
              </Paper>
            </motion.div>
          </Box>

          {/* Stats Section */}
          <motion.div variants={itemVariants}>
            <Box sx={{ textAlign: "center", mb: 6 }}>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: "bold",
                  color: "#1a1a2e",
                  mb: 2,
                  fontSize: { xs: "1.8rem", md: "2.2rem" },
                }}
              >
                {t.statsTitle}
              </Typography>
              <Divider
                sx={{
                  width: "60px",
                  margin: "0 auto",
                  height: "3px",
                  background: "#c9a03d",
                  borderRadius: "2px",
                }}
              />
            </Box>
          </motion.div>

          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flexWrap: "wrap",
              gap: { xs: 2, sm: 3, md: 3 },
              mb: 10,
            }}
          >
            {t.stats.map((stat, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                style={{
                  width: getValueCardWidth(),
                  maxWidth: isMobile ? "100%" : "250px",
                  minWidth: isMobile ? "auto" : "200px",
                }}
              >
                <Paper
                  elevation={3}
                  sx={{
                    p: 3,
                    textAlign: "center",
                    background: "linear-gradient(135deg, #1a1a2e 0%, #0f0f23 100%)",
                    color: "white",
                    borderRadius: "16px",
                  }}
                >
                  <Typography variant="h2" sx={{ fontSize: "2.5rem", fontWeight: "bold", color: "#c9a03d" }}>
                    {stat.value}
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {stat.label}
                  </Typography>
                </Paper>
              </motion.div>
            ))}
          </Box>

          {/* Why Choose Us Section */}
          <motion.div variants={itemVariants}>
            <Box sx={{ textAlign: "center", mb: 6 }}>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: "bold",
                  color: "#1a1a2e",
                  mb: 2,
                  fontSize: { xs: "1.8rem", md: "2.2rem" },
                }}
              >
                {t.whyChooseUs}
              </Typography>
              <Typography variant="body1" sx={{ color: "#666", mb: 2 }}>
                {t.whyChooseUsDesc}
              </Typography>
              <Divider
                sx={{
                  width: "60px",
                  margin: "0 auto",
                  height: "3px",
                  background: "#c9a03d",
                  borderRadius: "2px",
                }}
              />
            </Box>
          </motion.div>

          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "stretch",
              flexWrap: "wrap",
              gap: { xs: 2, sm: 3, md: 3 },
              mb: 10,
            }}
          >
            {t.features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={index}
                  variants={itemVariants}
                  style={{
                    width: getCardWidth(),
                    maxWidth: isMobile ? "100%" : "350px",
                    minWidth: isMobile ? "auto" : "280px",
                  }}
                >
                  <Paper
                    elevation={3}
                    sx={{
                      p: 3,
                      textAlign: "center",
                      background: "white",
                      borderRadius: "16px",
                      height: "100%",
                      transition: "0.3s",
                      "&:hover": {
                        transform: "translateY(-5px)",
                        boxShadow: "0 10px 30px rgba(201,160,61,0.15)",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 60,
                        height: 60,
                        borderRadius: "12px",
                        bgcolor: "rgba(201,160,61,0.1)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        margin: "0 auto 16px",
                      }}
                    >
                      <Icon sx={{ fontSize: 30, color: "#c9a03d" }} />
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1 }}>
                      {feature.title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#666" }}>
                      {feature.desc}
                    </Typography>
                  </Paper>
                </motion.div>
              );
            })}
          </Box>

          {/* Our Values Section */}
          <motion.div variants={itemVariants}>
            <Box sx={{ textAlign: "center", mb: 6 }}>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: "bold",
                  color: "#1a1a2e",
                  mb: 2,
                  fontSize: { xs: "1.8rem", md: "2.2rem" },
                }}
              >
                {t.ourValues}
              </Typography>
              <Divider
                sx={{
                  width: "60px",
                  margin: "0 auto",
                  height: "3px",
                  background: "#c9a03d",
                  borderRadius: "2px",
                }}
              />
            </Box>
          </motion.div>

          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "stretch",
              flexWrap: "wrap",
              gap: { xs: 2, sm: 3, md: 3 },
              mb: 10,
            }}
          >
            {t.values.map((value, index) => {
              const Icon = value.icon;
              return (
                <motion.div
                  key={index}
                  variants={itemVariants}
                  style={{
                    width: getCardWidth(),
                    maxWidth: isMobile ? "100%" : "350px",
                    minWidth: isMobile ? "auto" : "280px",
                  }}
                >
                  <Paper
                    elevation={2}
                    sx={{
                      p: 3,
                      textAlign: "center",
                      background: "#f8f9fa",
                      borderRadius: "16px",
                      height: "100%",
                      border: "1px solid rgba(201,160,61,0.2)",
                    }}
                  >
                    <Icon sx={{ fontSize: 40, color: "#c9a03d", mb: 1 }} />
                    <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1 }}>
                      {value.title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#666" }}>
                      {value.desc}
                    </Typography>
                  </Paper>
                </motion.div>
              );
            })}
          </Box>

          {/* Leadership Section */}
          <motion.div variants={itemVariants}>
            <Box sx={{ textAlign: "center", mb: 6 }}>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: "bold",
                  color: "#1a1a2e",
                  mb: 2,
                  fontSize: { xs: "1.8rem", md: "2.2rem" },
                }}
              >
                {t.leadershipTitle}
              </Typography>
              <Typography variant="body1" sx={{ color: "#666", mb: 2 }}>
                {t.leadershipDesc}
              </Typography>
              <Divider
                sx={{
                  width: "60px",
                  margin: "0 auto",
                  height: "3px",
                  background: "#c9a03d",
                  borderRadius: "2px",
                }}
              />
            </Box>
          </motion.div>

          <motion.div variants={itemVariants}>
            <Paper
              elevation={3}
              sx={{
                p: { xs: 3, md: 4 },
                borderRadius: "20px",
                background: "white",
                textAlign: "center",
                maxWidth: 500,
                mx: "auto",
                mb: 10,
              }}
            >
              <Avatar
                sx={{
                  width: 100,
                  height: 100,
                  bgcolor: "#c9a03d",
                  mx: "auto",
                  mb: 2,
                  fontSize: 40,
                }}
              >
                H
              </Avatar>
              <Typography variant="h5" sx={{ fontWeight: "bold", mb: 0.5 }}>
                {t.leaderName}
              </Typography>
              <Typography variant="subtitle1" sx={{ color: "#c9a03d", mb: 2 }}>
                {t.leaderTitle}
              </Typography>
              <Typography variant="body1" sx={{ color: "#666", lineHeight: 1.8 }}>
                {t.leaderDesc}
              </Typography>
            </Paper>
          </motion.div>

          {/* Call to Action Section */}
          <motion.div variants={itemVariants}>
            <Paper
              elevation={3}
              sx={{
                p: { xs: 4, md: 5 },
                borderRadius: "24px",
                background: "linear-gradient(135deg, #1a1a2e 0%, #0f0f23 100%)",
                color: "white",
                textAlign: "center",
                mb: 6,
              }}
            >
              <Typography variant="h4" sx={{ fontWeight: "bold", mb: 2 }}>
                {t.ctaTitle}
              </Typography>
              <Typography variant="body1" sx={{ mb: 3, opacity: 0.9 }}>
                {t.ctaDesc}
              </Typography>
              <Button
                variant="contained"
                onClick={() => window.location.href = "/"}
                sx={{
                  bgcolor: "#c9a03d",
                  color: "#1a1a2e",
                  borderRadius: "30px",
                  px: 4,
                  py: 1.5,
                  fontSize: "1rem",
                  fontWeight: "bold",
                  "&:hover": {
                    bgcolor: "#a07d2e",
                  },
                }}
              >
                {t.ctaButton}
              </Button>
            </Paper>
          </motion.div>

          {/* Footer Note */}
          <motion.div variants={itemVariants}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                background: "rgba(212,175,55,0.1)",
                borderRadius: "12px",
                border: "1px solid rgba(212,175,55,0.3)",
                textAlign: "center",
              }}
            >
              <Typography variant="caption" sx={{ color: "#666", fontSize: "0.75rem" }}>
                {t.footerNote}
              </Typography>
            </Paper>
          </motion.div>
        </motion.div>
      </Container>
    </Box>
  );
};

export default AboutUsPage;